/** Attaching a custom domain to a workspace, as a sequence rather than a call.
 *
 *  Lives here and not in the create page because the ordering is the whole
 *  rule and the ordering is what a component test cannot see: the platform's
 *  governance profile has `require_dns_proof` on, so
 *  `enforce_tenant_branding_policy` rejects a `custom_domain` that is not
 *  already on that tenant's verified list. Saving branding first therefore
 *  400s, every time, and the failure looks like a permissions problem rather
 *  than an ordering one.
 */

//: The add-on `branding_service.white_label_enabled` looks for. A custom domain
//: is stored without it but never served, so setting one has to grant it.
export const WHITE_LABEL_ADDON = 'white_label';

/** A hostname as the server will store it: no scheme, no port, no path.
 *  Mirrors `branding_service.normalise_host`. */
export const normaliseHost = (value) => {
  let host = String(value || '').trim().toLowerCase();
  if (!host) return '';
  if (host.includes('://')) host = host.split('://')[1];
  return host.split('/')[0].split(':')[0].replace(/\.+$/, '');
};

/** Whether this host belongs to the platform's own domain.
 *
 *  Such a host is not a custom domain and the API refuses to store one
 *  (`assert_custom_domain_allowed` raises 422): a workspace already answers on
 *  its portal subdomain with nothing configured. Checked client-side too
 *  because the tenant is created before the domain is attached, and a domain
 *  that was always going to be refused would cost a workspace that cannot be
 *  un-created. */
export const isPortalHost = (host, portal) => {
  const value = normaliseHost(host);
  const suffix = normaliseHost(portal);
  if (!value || !suffix) return false;
  return value === suffix || value.endsWith(`.${suffix}`);
};

/** The add-on list to send as `usage_limits_override.addons`.
 *
 *  `get_effective_tenant_limits` merges the override over the plan shallowly,
 *  so this REPLACES the plan's list rather than adding to it — the plan's own
 *  add-ons have to be carried across or the tenant silently loses them. */
export const addonsWithWhiteLabel = (baseAddons = [], wantsCustomDomain = false) => {
  const rows = Array.isArray(baseAddons) ? baseAddons : [];
  if (!wantsCustomDomain || rows.includes(WHITE_LABEL_ADDON)) return [...rows];
  return [...rows, WHITE_LABEL_ADDON];
};

/** Authorise the domain, then store it. Never the other way round.
 *
 *  Resolves to `{ ok: true, records, verifiedAt }` or, when a step fails,
 *  `{ ok: false, failedStep, error }` — the step is named because the two
 *  halves fail for different reasons and are fixed in different places.
 *  Deliberately does not throw: the caller has already created the tenant, and
 *  a rejected promise here reads as the whole creation having failed.
 */
export const attachCustomDomain = async (client, tenantId, domain) => {
  let step = 'authorising the domain';
  try {
    await client.updateTenantVerifiedDomains(tenantId, { domains: [domain] });
    step = 'saving the domain';
    const response = await client.updateTenantBranding(tenantId, { custom_domain: domain });
    return {
      ok: true,
      failedStep: null,
      error: null,
      records: response?.data?.dns_records || [],
      verifiedAt: response?.data?.custom_domain_verified_at || null,
    };
  } catch (error) {
    return { ok: false, failedStep: step, error, records: [], verifiedAt: null };
  }
};
